import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import CloseIcon from "assets/icons/close.svg?react";
import { useEffect, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import DropdownLogic from "components/DropdownLogic";
import NewInput from "components/NewInput";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import classNames from "classnames";
import DossierIcon from "assets/icons/dossierNew.svg?react";
import { ITag } from "dto/ITag";
import NotFoundScreen from "components/NotFoundScreen";
import Chip from "components/Chip";
import { useTranslation } from "react-i18next";

const SelectCaseFolders: FC<Props> = ({
  folders,
  setFolders,
  onClose,
  allowSearch = true,
  showButtons = false,
  showCounter = true,
  tags,
}) => {
  const { t } = useTranslation();

  const [showNewFolder, setShowNewFolder] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>(null);
  const [, setNewFolderFocused] = useState<boolean>(false);
  const refNewFolderInput = useRef(null);

  const [active, setActive] = useState(false);
  const [value, setValue] = useState("");

  const [search, setSearch] = useState<ITag[]>([]);

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (active) {
      setActive(false);
    }

    if (onClose) {
      onClose();
    }
  });

  useEffect(() => {
    if (!value.length) {
      setSearch(tags);
      return;
    }

    setSearch(
      tags.filter((tag: ITag) =>
        tag.Name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
      )
    );
  }, [tags, value]);

  const onChoose = (tag: ITag) => {
    if (!folders.find((x) => x === tag.Name)) {
      setFolders([...folders, tag.Name]);
    }
  };

  const cancelNewFolder = () => {
    setNewFolderName(null);
    setShowNewFolder(false);
  };

  const commitNewFolder = (label: string) => {
    setFolders([...folders, label]);
    setNewFolderName(null);
    setShowNewFolder(false);
  };

  const handleRemoveFolder = (name: string) => {
    setFolders(folders.filter((f) => f !== name));
  };

  useEffect(() => {
    if (refNewFolderInput?.current) {
      refNewFolderInput?.current.focus();
    }
  }, [refNewFolderInput?.current]);

  return (
    <div ref={outsideRef} className={styles.wrapper}>
      <div onClick={() => setActive(!active)} className={`${styles.card}`}>
        <DossierIcon className={`${styles.icon}`} />
        <div className={styles.text}>{t("selectCaseFolders.folders")}</div>
        {showCounter && folders.length > 0 && (
          <div className={styles.counter}>{folders.length}</div>
        )}

        {!onClose && (
          <FontAwesomeIcon
            className={styles.closeIcon}
            icon={active ? faCaretUp : faCaretDown}
          />
        )}
      </div>

      {active && (
        <DropdownLogic>
          <div className={styles.dropdown}>
            {allowSearch && !showNewFolder && (
              <div className={styles.search}>
                <NewInput
                  focus
                  labelClassName={styles.x}
                  className={styles.d}
                  value={value}
                  label={t("selectCaseFolders.searchFolder")}
                  onChange={(v) => setValue(v)}
                />
              </div>
            )}

            {showNewFolder && (
              <div className={styles.inlineEditor}>
                <input
                  ref={refNewFolderInput}
                  autoFocus={true}
                  className={styles.newFolderInput}
                  key="input_newFolderrr"
                  defaultValue={newFolderName}
                  onFocus={() => {
                    setNewFolderFocused(true);
                  }}
                  onBlur={(e) => {
                    setNewFolderName(e.target.value);
                    setNewFolderFocused(false);
                  }}
                  placeholder={t("dossier.newFolder")}
                  id="input_newFolder"
                  onKeyDownCapture={(e) => {
                    if (e.key === "Enter") {
                      const i = e.target as HTMLInputElement;
                      commitNewFolder(i.value);
                    } else if (e.key === "Escape") {
                      cancelNewFolder();
                    }
                    e.stopPropagation();
                  }}
                  onClickCapture={(e) => e.stopPropagation()}
                ></input>

                <HollowButton
                  className={styles.smallButton}
                  text={t("common.cancel")}
                  onClick={() => {
                    cancelNewFolder();
                  }}
                ></HollowButton>
                <Button
                  text={t("common.save")}
                  onClick={() =>
                    commitNewFolder(
                      (refNewFolderInput.current as HTMLInputElement).value
                    )
                  }
                  className={styles.smallButton}
                ></Button>
              </div>
            )}
            {!showNewFolder && (
              <div className={styles.buttons2}>
                <HollowButton
                  text={t("selectCaseFolders.addNewFolder")}
                  onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    setShowNewFolder(true);
                  }}
                  className={styles.hollowButton}
                ></HollowButton>
              </div>
            )}

            <div className={styles.choosen}>
              {folders.map((f) => (
                <Chip
                  key={f}
                  onDelete={() => handleRemoveFolder(f)}
                  element={<div>{f}</div>}
                ></Chip>
              ))}
            </div>

            <div className={styles.list}>
              {search.map((status) => (
                <div
                  key={status.Id}
                  className={classNames(styles.box)}
                  onClick={() => {
                    if (!showButtons) {
                      onChoose(status);
                      setActive(false);
                    }
                  }}
                >
                  {status.Name}
                </div>
              ))}

              {folders
                .filter((x) => !tags.find((t) => t.Name === x))
                .map((status) => (
                  <div
                    key={`new_${status}`}
                    className={classNames(styles.box, styles.selected)}
                  >
                    {status}
                    <CloseIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setFolders([...folders.filter((x) => x !== status)]);
                      }}
                    />
                  </div>
                ))}
            </div>
            {tags.length === 0 && (
              <div className={styles.empty}>
                <NotFoundScreen
                  title={t("emptyStates.noFolders")}
                  text={t("emptyStates.defineFoldersInSettings")}
                  size="medium"
                ></NotFoundScreen>
              </div>
            )}
          </div>
        </DropdownLogic>
      )}
    </div>
  );
};

export default SelectCaseFolders;
